import { FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useSelector } from 'react-redux';

import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { PdpRespSimilarItemsGrid } from '../../PdpRespSimilarItems/PdpRespSimilarItemsGrid';
import { flattenConnectionNodes } from '../../../utils/relayUtils';

import { moreFromThisSeller, viewAll } from '../../pdpMessages';
import { locations } from '../../../utils/tracking/shared/favoritesTracking';

import { PdpRespSellerItems_viewer$key } from './__generated__/PdpRespSellerItems_viewer.graphql';
import { PdpRespSellerItems_item$key } from './__generated__/PdpRespSellerItems_item.graphql';
import { PdpRespSellerItems_user$key } from './__generated__/PdpRespSellerItems_user.graphql';

import styles from './PdpRespSellerItems.scss';

const viewerFragment = graphql`
    fragment PdpRespSellerItems_viewer on Viewer {
        sellerGridItems: moreLikeThisItem(first: 8, seller: INCLUDE, itemId: $itemId) {
            edges {
                node {
                    serviceId
                    ...PdpRespSimilarItemsGrid_similarItems
                }
            }
        }
    }
`;

const itemFragment = graphql`
    fragment PdpRespSellerItems_item on Item {
        serviceId
        seller {
            serviceId
        }
        ...PdpRespSimilarItemsGrid_item
    }
`;

const userFragment = graphql`
    fragment PdpRespSellerItems_user on User {
        ...PdpRespSimilarItemsGrid_user
    }
`;

// TODO: replace with State type
type ReduxState = {
    relayVariables: {
        variables: {
            isClient: boolean;
            userId?: string | null;
        };
    };
};

export const PdpRespSellerItems: FC<{
    viewer: PdpRespSellerItems_viewer$key;
    item: PdpRespSellerItems_item$key;
    user: PdpRespSellerItems_user$key;
}> = ({ ...props }) => {
    const viewer = useFragment(viewerFragment, props.viewer);
    const item = useFragment(itemFragment, props.item);
    const user = useFragment(userFragment, props.user);
    const { isClient, userId } = useSelector((state: ReduxState) => state.relayVariables.variables);

    const itemPk = item.serviceId;
    const sellerPk = item.seller?.serviceId;

    if (!itemPk || !sellerPk) {
        return null;
    }

    return (
        <CurrencyProvider
            render={({ currency }: $TSFixMe) => (
                <PdpRespSimilarItemsGrid
                    hideBar
                    viewer={viewer}
                    item={item}
                    user={user}
                    relayVariables={{ userId, isClient }}
                    currency={currency}
                    favoritesLocation={locations.SELLER_GRID}
                    header={moreFromThisSeller}
                    subHeader={
                        <SeoLink
                            className={styles.viewAll}
                            dataTn="pdp-view-all-seller"
                            linkData={{
                                path: `/more-from-seller/?itemPk=${itemPk}&sellerPk=${sellerPk}`,
                                isLinkable: false,
                            }}
                        >
                            {viewAll}
                        </SeoLink>
                    }
                    gridLocation={'sold pdp more from seller'}
                    similarItems={flattenConnectionNodes(viewer.sellerGridItems)}
                    isDark={false}
                    gridHeaderClass={styles.gridHeader}
                    gridWrapperClass={styles.gridWrapper}
                />
            )}
        />
    );
};
